import React, { useState, useEffect } from 'react';
import {
  Shield,
  Settings,
  Save,
  TestTube,
  AlertCircle,
  Download,
  RefreshCw,
  Clock,
  Key
} from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';
import AdminSecurityHelpButton from './AdminSecurityHelpButton';

interface SSOConfig {
  provider: 'okta' | 'saml' | 'none';
  enabled: boolean;
  metadata: {
    entryPoint?: string;
    cert?: string;
    issuer?: string;
    oktaUrl?: string;
    oktaAppId?: string;
    oktaCert?: string;
    callbackUrl?: string;
  };
}


const SSOConfiguration: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'sso' | 'mfa' | 'password'>('sso');
  const [ssoConfig, setSSOConfig] = useState<SSOConfig>({
    provider: 'none',
    enabled: false,
    metadata: {}
  });
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mfaSettings, setMfaSettings] = useState({
    enforceForAdmins: true,
    enforceForAll: false,
    allowBackupCodes: true,
    backupCodeCount: 10
  });
  const [passwordSettings, setPasswordSettings] = useState<{
    password_expiration_days: number | null;
    available_options: { value: number | null; label: string; description: string }[];
  }>({
    password_expiration_days: null,
    available_options: []
  });
  const [tenantMfaSettings, setTenantMfaSettings] = useState({
    mfa_required: false,
    mfa_instruction: ''
  });
  const [savingTenantMfa, setSavingTenantMfa] = useState(false);

  useEffect(() => {
    fetchConfigurations();
  }, []);

  const fetchConfigurations = async () => {
    try {
      const ssoResponse = await api.get('/api/admin/config/sso');
      setSSOConfig(ssoResponse.data.config || { provider: 'none', enabled: false, metadata: {} });

      const mfaResponse = await api.get('/api/admin/config/mfa');
      setMfaSettings(mfaResponse.data.settings || mfaSettings);

      const passwordResponse = await api.get('/api/tenants/current/security-settings');
      if (passwordResponse.data.success) {
        setPasswordSettings({
          password_expiration_days: passwordResponse.data.security_settings.password_expiration_days,
          available_options: passwordResponse.data.security_settings.available_expiration_options || []
        });
        setTenantMfaSettings({
          mfa_required: passwordResponse.data.security_settings.mfa_required || false,
          mfa_instruction: passwordResponse.data.security_settings.mfa_instruction || ''
        });
      }
    } catch (error) {
      console.error('Error fetching configurations:', error);
      toast.error('Failed to load configurations');
    }
  };

  const handleSSOSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await api.put('/api/admin/config/sso', ssoConfig);
      toast.success('SSO configuration saved successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to save SSO configuration');
    } finally {
      setSaving(false);
    }
  };

  const testSSOConnection = async () => {
    setTesting(true);
    try {
      const response = await api.post('/api/admin/config/sso/test', ssoConfig);
      if (response.data.success) {
        toast.success('SSO connection test successful');
      } else {
        toast.error('SSO connection test failed');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const downloadSAMLMetadata = () => {
    window.open('/api/sso/metadata', '_blank');
  };

  const handleMFASettingsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      await api.put('/api/admin/config/mfa', mfaSettings);
      toast.success('MFA settings saved successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to save MFA settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSettingsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      await api.put('/api/tenants/current/security-settings', {
        password_expiration_days: passwordSettings.password_expiration_days
      });
      toast.success('Password expiration settings saved successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to save password settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTenantMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingTenantMfa(true);
    try {
      await api.put('/api/tenants/current/security-settings', {
        mfa_required: tenantMfaSettings.mfa_required,
        mfa_instruction: tenantMfaSettings.mfa_instruction
      });
      toast.success('Tenant MFA policy saved successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to save tenant MFA policy');
    } finally {
      setSavingTenantMfa(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header — lives above the SSO/MFA/Password sub-tab bar so
          that a single help icon covers the entire SSO & Logins screen
          without needing a per-sub-tab icon. */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-lg">
            <Shield className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">SSO & Logins</h2>
            <p className="text-sm text-gray-500">
              Configure Single Sign-On, multi-factor authentication, and password policies
            </p>
          </div>
        </div>
        <AdminSecurityHelpButton screenId="sso-logins" />
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('sso')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'sso'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Shield className="inline w-4 h-4 mr-2" />
            SSO Configuration
          </button>
          <button
            onClick={() => setActiveTab('mfa')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'mfa'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Settings className="inline w-4 h-4 mr-2" />
            MFA Settings
          </button>
          <button
            onClick={() => setActiveTab('password')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'password'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Key className="inline w-4 h-4 mr-2" />
            Password Policy
          </button>
        </nav>
      </div>

      {/* SSO Configuration Tab */}
      {activeTab === 'sso' && (
        <div className="bg-white rounded-lg shadow border p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Single Sign-On (SSO) Configuration</h3>
          
          <form onSubmit={handleSSOSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SSO Provider
              </label>
              <select
                value={ssoConfig.provider}
                onChange={(e) => setSSOConfig(prev => ({ 
                  ...prev, 
                  provider: e.target.value as 'okta' | 'saml' | 'none' 
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="none">None (Local Authentication)</option>
                <option value="okta">Okta</option>
                <option value="saml">Generic SAML 2.0</option>
              </select>
            </div>

            {ssoConfig.provider === 'okta' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Okta Domain
                  </label>
                  <input
                    type="text"
                    value={ssoConfig.metadata.oktaUrl || ''}
                    onChange={(e) => setSSOConfig(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, oktaUrl: e.target.value }
                    }))}
                    placeholder="https://your-domain.okta.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Okta App ID
                  </label>
                  <input
                    type="text"
                    value={ssoConfig.metadata.oktaAppId || ''}
                    onChange={(e) => setSSOConfig(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, oktaAppId: e.target.value }
                    }))}
                    placeholder="0oa1b2c3d4e5f6g7h8i9"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Okta Certificate
                  </label>
                  <textarea
                    value={ssoConfig.metadata.oktaCert || ''}
                    onChange={(e) => setSSOConfig(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, oktaCert: e.target.value }
                    }))}
                    placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-xs"
                  />
                </div>
              </>
            )}

            {ssoConfig.provider === 'saml' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SAML Entry Point URL
                  </label>
                  <input
                    type="url"
                    value={ssoConfig.metadata.entryPoint || ''}
                    onChange={(e) => setSSOConfig(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, entryPoint: e.target.value }
                    }))}
                    placeholder="https://idp.example.com/sso/saml"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SAML Certificate
                  </label>
                  <textarea
                    value={ssoConfig.metadata.cert || ''}
                    onChange={(e) => setSSOConfig(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, cert: e.target.value }
                    }))}
                    placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-xs"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Issuer
                  </label>
                  <input
                    type="text"
                    value={ssoConfig.metadata.issuer || 'jarvis-rcsa'}
                    onChange={(e) => setSSOConfig(prev => ({
                      ...prev,
                      metadata: { ...prev.metadata, issuer: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </>
            )}

            {ssoConfig.provider !== 'none' && (
              <>
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-blue-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">Service Provider Information</h3>
                      <div className="mt-2 text-sm text-blue-700">
                        <p>Use these URLs in your Identity Provider configuration:</p>
                        <ul className="list-disc list-inside mt-2 space-y-1">
                          <li>Callback URL: {window.location.origin}/api/sso/{ssoConfig.provider}/callback</li>
                          <li>Metadata URL: {window.location.origin}/api/sso/metadata</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="ssoEnabled"
                    checked={ssoConfig.enabled}
                    onChange={(e) => setSSOConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                    className="mr-2"
                  />
                  <label htmlFor="ssoEnabled" className="text-sm text-gray-700">
                    Enable SSO Authentication
                  </label>
                </div>
              </>
            )}

            <div className="flex justify-between pt-4">
              <div className="flex gap-2">
                {ssoConfig.provider !== 'none' && (
                  <>
                    <button
                      type="button"
                      onClick={testSSOConnection}
                      disabled={testing}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
                    >
                      <TestTube className={`w-4 h-4 ${testing ? 'animate-spin' : ''}`} />
                      Test Connection
                    </button>
                    <button
                      type="button"
                      onClick={downloadSAMLMetadata}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download Metadata
                    </button>
                  </>
                )}
              </div>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Configuration
              </button>
            </div>
          </form>
        </div>
      )}


      {/* MFA Settings Tab */}
      {activeTab === 'mfa' && (
        <div className="bg-white rounded-lg shadow border p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Multi-Factor Authentication Settings</h3>
          
          <form onSubmit={handleMFASettingsSubmit} className="space-y-4">
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={mfaSettings.enforceForAdmins}
                  onChange={(e) => setMfaSettings(prev => ({ 
                    ...prev, 
                    enforceForAdmins: e.target.checked 
                  }))}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">
                  Enforce MFA for all Admin and Master users
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={mfaSettings.enforceForAll}
                  onChange={(e) => setMfaSettings(prev => ({ 
                    ...prev, 
                    enforceForAll: e.target.checked 
                  }))}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">
                  Enforce MFA for all users
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={mfaSettings.allowBackupCodes}
                  onChange={(e) => setMfaSettings(prev => ({ 
                    ...prev, 
                    allowBackupCodes: e.target.checked 
                  }))}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">
                  Allow backup codes for MFA recovery
                </span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Number of Backup Codes
              </label>
              <input
                type="number"
                min="5"
                max="20"
                value={mfaSettings.backupCodeCount}
                onChange={(e) => setMfaSettings(prev => ({ 
                  ...prev, 
                  backupCodeCount: parseInt(e.target.value) 
                }))}
                className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-blue-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">MFA Implementation</h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>TOTP-based MFA is implemented with:</p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>Support for Google Authenticator, Authy, and other TOTP apps</li>
                      <li>Backup codes for account recovery</li>
                      <li>Device fingerprinting for trusted devices</li>
                      <li>Account lockout after failed attempts</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save MFA Settings
              </button>
            </div>
          </form>

          {/* Per-Tenant MFA Requirement */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h4 className="text-base font-medium text-gray-900 mb-1">Tenant MFA Policy</h4>
            <p className="text-sm text-gray-500 mb-4">
              When enabled, MFA becomes mandatory for all <strong>Admin and Super Admin</strong> users.
              Users without MFA will be forced to set it up on their next login.
            </p>
            <form onSubmit={handleTenantMfaSubmit} className="space-y-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={tenantMfaSettings.mfa_required}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      setTenantMfaSettings(prev => ({
                        ...prev,
                        mfa_required: enabled,
                        mfa_instruction: enabled && !prev.mfa_instruction
                          ? 'Any MFA tool you currently use is fine. Just scan this QR code to set it up from your app.'
                          : prev.mfa_instruction
                      }));
                    }}
                    className="sr-only peer"
                    id="tenant-mfa-toggle"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
                </div>
                <span className="text-sm font-medium text-gray-700">
                  Require MFA for all Admins
                </span>
                {tenantMfaSettings.mfa_required && (
                  <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">Mandatory</span>
                )}
              </label>

              {tenantMfaSettings.mfa_required && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    On-screen instruction for users (shown on MFA setup page)
                  </label>
                  <textarea
                    value={tenantMfaSettings.mfa_instruction}
                    onChange={(e) => setTenantMfaSettings(prev => ({ ...prev, mfa_instruction: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    This message will appear at the top of the MFA setup screen for Admins. You can customize it for your organization.
                  </p>
                </div>
              )}

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={savingTenantMfa}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 text-sm"
                >
                  {savingTenantMfa ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save Tenant MFA Policy
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {activeTab === 'password' && (
        <div className="bg-white rounded-lg shadow border p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Password Expiration Policy</h3>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3" />
              <div>
                <h4 className="text-sm font-medium text-blue-900">NIST Guidelines</h4>
                <p className="text-sm text-blue-700 mt-1">
                  NIST SP 800-63B recommends against periodic password rotation unless there is evidence of compromise.
                  However, some regulatory frameworks (e.g., banking, healthcare) may require periodic password changes.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handlePasswordSettingsSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Password Expiration Period
              </label>
              <div className="space-y-3">
                {[
                  { value: null, label: 'Disabled (NIST-aligned)', description: 'No forced password rotation - recommended for most organizations', icon: '✓' },
                  { value: 30, label: '30 days', description: 'High-security environments with strict compliance requirements', icon: '🔒' },
                  { value: 60, label: '60 days', description: 'Balanced approach for regulated industries', icon: '🔐' },
                  { value: 90, label: '90 days', description: 'Standard compliance requirement for many banking regulations', icon: '📋' }
                ].map((option) => (
                  <label
                    key={String(option.value)}
                    className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      passwordSettings.password_expiration_days === option.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="password_expiration"
                      checked={passwordSettings.password_expiration_days === option.value}
                      onChange={() => setPasswordSettings(prev => ({
                        ...prev,
                        password_expiration_days: option.value
                      }))}
                      className="mt-1 mr-3"
                    />
                    <div className="flex-1">
                      <div className="flex items-center">
                        <span className="mr-2">{option.icon}</span>
                        <span className="font-medium text-gray-900">{option.label}</span>
                        {option.value === null && (
                          <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">
                            Recommended
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{option.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {passwordSettings.password_expiration_days !== null && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <Clock className="w-5 h-5 text-yellow-600 mt-0.5 mr-3" />
                  <div>
                    <h4 className="text-sm font-medium text-yellow-900">Expiration Behavior</h4>
                    <ul className="text-sm text-yellow-700 mt-1 list-disc list-inside space-y-1">
                      <li>Users will be prompted to change their password after {passwordSettings.password_expiration_days} days</li>
                      <li>A warning will be logged 7 days before expiration</li>
                      <li>Expired passwords will trigger the forced password change flow at login</li>
                      <li>This setting applies to all users in your organization</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end pt-4">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Password Policy
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default SSOConfiguration;